import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useState } from 'react';

import { isEmpty, noop } from 'lodash';

import { ChatDocumentContextType, ClaimDocumentMetadata, ClaimDocumentsResponse } from '../types.ts';

import { useClaimContext } from '~/components/Claim/ClaimContext.tsx';
import { post } from '~/utils/request';

interface ChatDocumentProviderProps {
  children: ReactNode;
}

const ChatDocumentContext = createContext<ChatDocumentContextType | null>(null);

export const useChatDocumentContext = () => {
  const ctx = useContext(ChatDocumentContext);
  if (!ctx) throw new Error('useChatDocumentContext must be used within a ChatDocumentProvider');
  return ctx;
};

export const ClaimDocumentProvider: React.FC<ChatDocumentProviderProps> = ({ children }) => {
  const [documents, setDocuments] = useState<Record<string, ClaimDocumentMetadata>>({});
  const [attemptedDocumentIds, setAttemptedDocumentIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { claimNumber } = useClaimContext();

  /**
   * Fetches all pages of documents for a claim using pagination
   */
  const fetchAllDocumentPages = useCallback(async (claimNumber: string): Promise<ClaimDocumentMetadata[]> => {
    const allDocuments: ClaimDocumentMetadata[] = [];
    let currentPage = 1;

    while (true) {
      const response = await post<ClaimDocumentsResponse>({
        url: `/api/v1/claims/${claimNumber}/documents`,
        data: {
          page: currentPage,
          limit: 100, // Use maximum page size to minimize requests
        },
      });

      allDocuments.push(...response.documents);

      if (!response.pagination.has_more) break;
      currentPage++;
    }

    return allDocuments;
  }, []);

  const fetchDocumentsByIds = useCallback(
    async (documentIds: string[]) => {
      if (isEmpty(documentIds)) return;

      // Filter out already attempted documents (whether successfully fetched or not)
      const missingDocumentIds = documentIds.filter((id) => !attemptedDocumentIds.has(id));
      if (isEmpty(missingDocumentIds)) return;

      setIsLoading(true);
      setError(null);

      try {
        // Mark these IDs as attempted before making the API call
        setAttemptedDocumentIds((prev) => new Set([...prev, ...missingDocumentIds]));

        const response = await post<ClaimDocumentsResponse>({
          url: `/api/v1/claims/${claimNumber}/documents/by_ids`,
          data: missingDocumentIds,
        });

        // Update documents map with new documents
        const newDocuments = Object.fromEntries(response.documents.map((doc) => [doc.id, doc]));
        setDocuments((prev) => ({ ...prev, ...newDocuments }));
      } catch (error) {
        setError('Failed to fetch document metadata. Please try again.');
        console.error('Error fetching documents:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [claimNumber, attemptedDocumentIds]
  );

  const fetchDocumentsByClaimNumber = useCallback(async (): Promise<string[]> => {
    if (!claimNumber) return [];

    setIsLoading(true);
    setError(null);

    try {
      const allDocuments = await fetchAllDocumentPages(claimNumber);
      const fetchedDocumentIds = allDocuments.map((doc) => doc.id);

      // Mark IDs as attempted immediately to prevent race conditions
      setAttemptedDocumentIds((prev) => new Set([...prev, ...fetchedDocumentIds]));

      // Update documents map
      const newDocuments = Object.fromEntries(allDocuments.map((doc) => [doc.id, doc]));
      setDocuments((prev) => ({ ...prev, ...newDocuments }));

      return fetchedDocumentIds;
    } catch (error) {
      setError('Failed to fetch document metadata. Please try again.');
      console.error('Error fetching documents:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [claimNumber, fetchAllDocumentPages]);

  const getDocumentsByIds = useCallback(
    async (documentIds: string[]): Promise<ClaimDocumentMetadata[]> => {
      if (isEmpty(documentIds)) return [];

      // Check which documents we need to fetch
      const missingDocumentIds = documentIds.filter((id) => !attemptedDocumentIds.has(id));

      // Fetch missing documents if any
      if (!isEmpty(missingDocumentIds)) {
        await fetchDocumentsByIds(missingDocumentIds);
      }

      // Return available documents
      return documentIds.map((id) => documents[id]).filter((doc): doc is ClaimDocumentMetadata => doc !== undefined);
    },
    [documents, attemptedDocumentIds, fetchDocumentsByIds]
  );

  const getClaimDocuments = useCallback(async (): Promise<string[]> => {
    return await fetchDocumentsByClaimNumber();
  }, [fetchDocumentsByClaimNumber]);

  const reset = useCallback(() => {
    setDocuments({});
    setAttemptedDocumentIds(new Set());
    setError(null);
  }, []);

  return (
    <ChatDocumentContext.Provider
      value={{
        documents,
        getDocumentsByIds,
        getClaimDocuments,
        isLoading,
        error,
        reset,
      }}
    >
      {children}
    </ChatDocumentContext.Provider>
  );
};

export const AccountDocumentProvider: React.FC<ChatDocumentProviderProps> = ({ children }) => {
  return (
    <ChatDocumentContext.Provider
      value={{
        documents: {},
        getDocumentsByIds: async (_: string[]) => {
          throw new Error('AccountDocumentProvider does not support getDocumentsByIds');
        },
        getClaimDocuments: async () => {
          throw new Error('AccountDocumentProvider does not support getClaimDocuments');
        },
        isLoading: false,
        error: null,
        reset: noop,
      }}
    >
      {children}
    </ChatDocumentContext.Provider>
  );
};

export const withAccountDocuments = <T extends object>(Component: React.ComponentType<T>): React.ComponentType<T> => {
  const WrappedComponent = (props: T) => (
    <AccountDocumentProvider>
      <Component {...props} />
    </AccountDocumentProvider>
  );

  WrappedComponent.displayName = `withAccountDocuments(${Component.displayName || Component.name})`;
  return WrappedComponent;
};
